/**
 * N of M — §7.7's "the owner sees an N-of-M view", rendered.
 *
 * Three things this component refuses to do, each because the alternative is a
 * number somebody would act on.
 *
 * **A null `progress` is not zero.** The API nulls the numbers out for anyone
 * who is neither the owner nor a holder of `document.publish` on the node, and
 * says so through `progressVisible`. Drawing an empty bar for that case would
 * tell a reader that nobody has acknowledged the document — the most alarming
 * possible reading of "you are not allowed to know". The redaction has its own
 * sentence, and the sentence says whose view this is.
 *
 * **A complete cycle with an empty audience is not a tick.** An audience of zero
 * is complete by arithmetic — 0 of 0 — and a `space_grant` cycle opened before
 * anybody holds the grant is exactly that, which is the auto-enrolment case seen
 * from the start rather than the middle. So the size is always rendered beside
 * the verdict, and a zero audience gets said out loud.
 *
 * **People who signed and then left the audience are counted apart.** Folding
 * them into N against a shrinking M produces "9 of 8"; dropping them hides that
 * the cycle was once further along than it looks. They get their own line.
 */

import type { AcknowledgmentProgress } from '../../api/types.ts';
import { Badge } from '../../components/ui/badge.tsx';

function percent(progress: AcknowledgmentProgress): number {
  if (progress.audienceSize === 0) return 0;
  return Math.round((progress.acknowledged / progress.audienceSize) * 100);
}

/**
 * The bar.
 *
 * Decorative, and labelled anyway: every number it draws is stated in text
 * beside it, so a screen reader gets the sentence rather than a shape. One fill
 * colour rather than a green-when-done variant — the tokens carry no solid
 * success background, and the verdict is a badge that says the word.
 */
function Meter({ progress }: { progress: AcknowledgmentProgress }) {
  return (
    <div
      className="h-2 w-full overflow-hidden rounded-full bg-tertiary"
      role="img"
      aria-label={`${progress.acknowledged} of ${progress.audienceSize} acknowledged`}
    >
      <div className="h-full bg-brand-solid" style={{ width: `${percent(progress)}%` }} />
    </div>
  );
}

export function CycleProgressView({
  progress,
  compact = false,
}: {
  progress: AcknowledgmentProgress | null;
  compact?: boolean;
}) {
  if (progress === null) {
    // Compact still has to say *why* there is no number, and a table cell is no
    // place for the paragraph — so the short form carries the sentence in its
    // tooltip rather than dropping it.
    return compact ? (
      <Badge tone="outline">
        <span title="§7.7 gives the N-of-M view to the document's owner and to whoever can publish it, and to nobody else.">
          Not shown to you
        </span>
      </Badge>
    ) : (
      <p className="text-sm text-tertiary">
        The numbers are not shown to you. §7.7 gives the N-of-M view to the document&apos;s owner
        and to whoever can publish it, and to nobody else — a deliverable published into a client
        space would otherwise render the agency&apos;s own compliance figures on a client&apos;s
        screen.
      </p>
    );
  }

  if (compact) {
    return (
      <span className="flex flex-wrap items-center gap-2 text-sm">
        <span className={progress.complete ? 'text-success-primary' : 'text-primary'}>
          {progress.acknowledged} of {progress.audienceSize}
        </span>
        {progress.audienceSize === 0 ? (
          <Badge tone="outline">Empty audience</Badge>
        ) : progress.complete ? (
          <Badge tone="success">Complete</Badge>
        ) : (
          <Badge tone="warning">{progress.outstanding} outstanding</Badge>
        )}
      </span>
    );
  }

  return (
    <div className="flex flex-col gap-2">
      <p className="flex flex-wrap items-baseline gap-2">
        <span className="text-display-xs font-semibold text-primary">
          {progress.acknowledged} of {progress.audienceSize}
        </span>
        <span className="text-sm text-tertiary">
          {progress.audienceSize === 1
            ? 'person in the audience has'
            : 'people in the audience have'}{' '}
          acknowledged this version
        </span>
      </p>

      <Meter progress={progress} />

      <div className="flex flex-wrap items-center gap-2 text-sm text-secondary">
        {progress.audienceSize === 0 ? (
          <Badge tone="outline">
            <span title="Nobody resolves into this audience right now. It is complete by arithmetic, not by agreement.">
              Nobody is in this audience yet
            </span>
          </Badge>
        ) : progress.complete ? (
          <Badge tone="success">Everyone in the audience has acknowledged</Badge>
        ) : (
          <Badge tone="warning">
            {progress.outstanding} {progress.outstanding === 1 ? 'person has' : 'people have'} not
          </Badge>
        )}
      </div>

      {progress.acknowledgedOutsideAudience > 0 ? (
        <p className="text-xs text-tertiary">
          {progress.acknowledgedOutsideAudience} more{' '}
          {progress.acknowledgedOutsideAudience === 1 ? 'person has' : 'people have'} acknowledged
          this version and no longer resolve into the audience — a changed role, or a revoked grant.
          Their acknowledgment is retained because it records something that happened; it is counted
          here rather than in the total, which is what keeps the total from reading &quot;9 of
          8&quot;.
        </p>
      ) : null}

      {progress.audienceSize > 0 ? (
        <p className="text-xs text-tertiary">
          Both numbers are re-resolved on every read. Nothing is cached, which is what makes a
          person provisioned this morning appear in the denominator of a cycle opened last month
          (§7.7).
        </p>
      ) : null}
    </div>
  );
}
